import { Project } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, TableCell } from '@/components';

interface ColumnProps {
  onDeleteClick: (projectId: number, projectName: string) => void;
  onViewDetailsClick: (project: Project) => void;
  onToggleVisibilityClick: (project: Project) => void;
  onEditServicesClick: (project: Project) => void;
  isTogglingVisibility: boolean;
  isDeleting: boolean;
}

export const createProjectColumns = ({
  onDeleteClick,
  onViewDetailsClick,
  onToggleVisibilityClick,
  onEditServicesClick,
  isTogglingVisibility,
  isDeleting,
}: ColumnProps): ColumnDef<Project>[] => [
  {
    accessorKey: 'id',
    header: 'Id',
    cell: ({ row }) => <TableCell>{row.original.id}</TableCell>,
    enableSorting: false,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => <TableCell truncateAfter={20}>{row.original.name}</TableCell>,
    size: 200,
    enableSorting: false,
  },
  {
    accessorKey: 'organizationName',
    header: 'Organization',
    cell: ({ row }) => <TableCell>{row.original.organizationName}</TableCell>,
    enableSorting: false,
  },
  {
    id: 'services',
    header: 'Services',
    size: 200,
    cell: ({ row }) => (
      <div className="whitespace-normal">{row.original.activeServices.join(', ')}</div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'lastUploadedPackage',
    header: 'Last uploaded',
    cell: ({ row }) => (
      <TableCell>
        {row.original.lastUploadedPackage
          ? new Date(row.original.lastUploadedPackage).toLocaleDateString('nb-NO', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '—'}
      </TableCell>
    ),
    enableSorting: false,
  },
  {
    id: 'unpublishedPackages',
    header: 'Unpublished packages',
    size: 250,
    cell: ({ row }) => {
      const { documentPackages, layoutPackages } = row.original.unpublishedPackages;
      const all = [...documentPackages, ...layoutPackages];
      if (all.length === 0) {
        return <TableCell>No unpublished packages</TableCell>;
      }
      return (
        <div className="flex flex-col gap-1 whitespace-normal">
          {documentPackages.length > 0 && (
            <div>
              <span className="font-semibold">Document Packages:</span>{' '}
              {documentPackages.join(', ')}
            </div>
          )}
          {layoutPackages.length > 0 && (
            <div>
              <span className="font-semibold">Layout Packages:</span> {layoutPackages.join(', ')}
            </div>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'isHidden',
    header: 'Visibility',
    cell: ({ row }) => (
      <TableCell>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${row.original.isHidden ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}
        >
          {row.original.isHidden ? 'Hidden' : 'Public'}
        </span>
      </TableCell>
    ),
    enableSorting: false,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <ActionButton
        icon={<BsThreeDots />}
        actions={[
          {
            label: 'View Details',
            onClick: () => onViewDetailsClick(row.original),
          },
          {
            label: 'Edit',
            onClick: () => onEditServicesClick(row.original),
          },
          {
            label: isTogglingVisibility
              ? 'Updating...'
              : row.original.isHidden
                ? 'Make Public'
                : 'Make Hidden',
            onClick: () => onToggleVisibilityClick(row.original),
            disabled: isTogglingVisibility,
          },
          {
            label: 'Delete',
            onClick: () => onDeleteClick(row.original.id, row.original.name),
            disabled: isDeleting,
            destructive: true,
          },
        ]}
      />
    ),
    enableSorting: false,
  },
];
